// WalkInRegisterModal — "Visitante sin QR" (roadmap item E, 2026-09-16).
// Guard registers a visitor without a QR: POST /api/security/walk-ins creates
// an access_code (origin 'walk_in', status 'pending_approval'). After creation
// the code string is shown prominently with clear "waiting for host" messaging
// and the 15-min auto-cancel caveat. Codes defensively against the backend
// contract (backend child lane, may not be deployed yet → 404 surfaces a
// friendly error, form stays usable).

import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, UserPlus, CheckCircle, Clock, AlertTriangle, Car, Phone, Camera, CreditCard } from 'lucide-react';
import { compressImageToDataUrl } from '../../utils/imageCompress';
import { walkInApi, normalizeWalkIn, type WalkIn } from '../../services/accessApi';
import { unitsAPI } from '../../services/api';

const DURATIONS = [1, 2, 4, 12, 24];

interface UnitOption { id: string; label: string }

export default function WalkInRegisterModal({
  open,
  onClose,
  condoId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  condoId?: string | null;
  onCreated?: (w: WalkIn) => void;
}) {
  const [visitorName, setVisitorName] = useState('');
  const [phone, setPhone] = useState('');
  // Walk-in enhancement (Jaime 2026-09-16): optional ID document number.
  const [idNumber, setIdNumber] = useState('');
  // Walk-in enhancement (Jaime 2026-09-16): ID photo capture. Reuses the exact
  // mechanism from the QR module (VerifyQR "Capturar ID"): file/camera pick →
  // compressImageToDataUrl → JPEG data URL sent inline in the walk-ins POST
  // payload as `visitor_photo` (same shape /access/id-capture accepts).
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const [plate, setPlate] = useState('');
  const [reason, setReason] = useState('');
  // Jaime 2026-09-16: default duration chip is now 1 hour (pre-selected).
  const [durationHours, setDurationHours] = useState(1);
  const [unitId, setUnitId] = useState('');
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<WalkIn | null>(null);

  // Load condo units for the selector (defensive: both units endpoints alias).
  useEffect(() => {
    if (!open || !condoId || units.length > 0) return;
    let alive = true;
    setUnitsLoading(true);
    (unitsAPI.getAll(condoId) as any)
      .catch(() => unitsAPI.getForCondo(condoId) as any)
      .then((res: any) => {
        if (!alive) return;
        const payload = res?.data?.data ?? res?.data;
        const list = payload?.units ?? payload?.data ?? (Array.isArray(payload) ? payload : []);
        setUnits((Array.isArray(list) ? list : [])
          .map((u: any) => ({
            id: u?.id || u?.unitId || '',
            label: u?.unit_label || u?.unitLabel || u?.label || u?.unit_number || u?.unitNumber || u?.number || 'Unidad',
          }))
          .filter((u: UnitOption) => u.id));
      })
      .catch(() => { if (alive) setUnits([]); })
      .finally(() => { if (alive) setUnitsLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, condoId]);

  // UX-3 (Jaime 2026-09-16): "Llamar residente" contacts in the success
  // dialog. GET /api/security/walk-ins/contacts?unitId= → [{ name, phone }]
  // (resident-class members only; phone may be null). The backend deploys in
  // parallel — a 404 while it rolls out just hides the section.
  const [unitContacts, setUnitContacts] = useState<{ name: string; phone: string | null }[]>([]);
  const [contactsState, setContactsState] = useState<'idle' | 'loading' | 'ok' | 'unavailable'>('idle');
  useEffect(() => {
    if (!created) { setContactsState('idle'); setUnitContacts([]); return; }
    const targetUnit = unitId || created.unit_id || '';
    if (!targetUnit) { setContactsState('unavailable'); return; }
    let alive = true;
    setContactsState('loading');
    setUnitContacts([]);
    walkInApi.contacts(targetUnit)
      .then((res: any) => {
        if (!alive) return;
        const payload = res?.data?.data ?? res?.data;
        // Backend contract: { unit_id, unit_label, condo_id, contacts: [...], count }.
        // BUG 2026-09-16: payload is an object, not an array — extract .contacts
        // (fall back to a bare array payload for older deployments).
        const list = payload?.contacts ?? (Array.isArray(payload) ? payload : []);
        setUnitContacts((Array.isArray(list) ? list : []).map((c: any) => ({
          name: String(c?.name ?? 'Residente'),
          phone: c?.phone ? String(c.phone) : null,
        })));
        setContactsState('ok');
      })
      .catch(() => { if (alive) setContactsState('unavailable'); });
    return () => { alive = false; };
  }, [created, unitId]);

  const codeString = useMemo(() => created?.access_code || created?.code || '', [created]);

  if (!open) return null;

  const reset = () => {
    setVisitorName(''); setPhone(''); setPlate(''); setReason('');
    setIdNumber(''); setPhotoDataUrl(null); setPhotoError(null);
    setDurationHours(1); setUnitId(''); setError(null); setCreated(null);
  };

  // Same client-side compression policy as VerifyQR's ID capture (HTTP 413 fix):
  // downscale to 1600px, JPEG q0.8 (~200-400 KB), EXIF stripped by re-encode.
  const handlePhotoFile = async (file: File | undefined | null) => {
    if (!file) return;
    setPhotoError(null);
    if (file.size > 25 * 1024 * 1024) {
      setPhotoError('La imagen supera el máximo de 25 MB.');
      return;
    }
    setPhotoBusy(true);
    try {
      const dataUrl = await compressImageToDataUrl(file, { maxDimension: 1600, quality: 0.8 });
      setPhotoDataUrl(dataUrl);
    } catch {
      setPhotoError('No se pudo procesar la foto. La visita se puede registrar sin ella.');
      setPhotoDataUrl(null);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleClose = () => { reset(); onClose(); };

  const submit = async () => {
    if (!visitorName.trim() || !unitId || !condoId) {
      setError('Completa los campos obligatorios (visitante y unidad).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await walkInApi.create({
        unitId,
        visitorName: visitorName.trim(),
        phone: phone.trim() || undefined,
        visitor_id_number: idNumber.trim() || undefined,
        visitor_photo: photoDataUrl || undefined,
        plate: plate.trim() || undefined,
        reason: reason.trim() || undefined,
        durationHours,
        condoId,
      });
      const payload = res.data?.data ?? res.data;
      const row = payload?.walk_in ?? payload?.walkIn ?? payload;
      const w = normalizeWalkIn(row);
      setCreated(w);
      onCreated?.(w);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setError('El registro de visitas sin QR aún no está disponible en el servidor.');
      } else {
        setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Error al registrar la visita');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Registrar visitante sin QR"
    >
      <div className="relative w-full max-w-md my-4 bg-gray-900 rounded-3xl border border-gray-700 p-6">
        <button
          type="button"
          aria-label="Cerrar"
          onClick={handleClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-gray-800 text-gray-300 hover:text-white text-xl leading-none flex items-center justify-center border border-gray-600"
        >
          ×
        </button>

        {!created ? (
          <>
            <div className="flex items-center gap-3 mb-1">
              <UserPlus className="w-6 h-6 text-[#36CCD8]" />
              <h2 className="text-lg font-bold text-white">Visitante sin QR</h2>
            </div>
            <p className="text-gray-400 text-sm mb-5">
              Registra al visitante y pide aprobación al residente. El código se activa cuando el anfitrión aprueba.
            </p>

            <label className="block text-gray-400 text-sm mb-1">Nombre del visitante *</label>
            <input
              type="text"
              value={visitorName}
              onChange={(e) => setVisitorName(e.target.value)}
              placeholder="Ej: Juan Pérez"
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-xl border border-gray-700 focus:border-[#36CCD8] focus:outline-none placeholder-gray-500 mb-4"
            />

            <label className="block text-gray-400 text-sm mb-1">Unidad a visitar *</label>
            <select
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              disabled={unitsLoading}
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-xl border border-gray-700 focus:border-[#36CCD8] focus:outline-none mb-4 disabled:opacity-60"
            >
              <option value="">{unitsLoading ? 'Cargando unidades…' : 'Selecciona una unidad'}</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>{u.label}</option>
              ))}
            </select>

            <label className="block text-gray-400 text-sm mb-1 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Teléfono</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Opcional"
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-xl border border-gray-700 focus:border-[#36CCD8] focus:outline-none placeholder-gray-500 mb-4"
            />

            <label className="block text-gray-400 text-sm mb-1 flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Cédula / ID #</label>
            <input
              type="text"
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value)}
              placeholder="Opcional"
              maxLength={40}
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-xl border border-gray-700 focus:border-[#36CCD8] focus:outline-none placeholder-gray-500 mb-4"
            />

            <label className="block text-gray-400 text-sm mb-1 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Foto de identificación</label>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => { handlePhotoFile(e.target.files?.[0]); e.currentTarget.value = ''; }}
            />
            <div className="flex items-center gap-3 mb-1">
              {photoDataUrl ? (
                <img src={photoDataUrl} alt="Identificación del visitante" className="w-20 h-14 object-cover rounded-lg border border-gray-600" />
              ) : (
                <div className="w-20 h-14 rounded-lg bg-gray-800 border border-dashed border-gray-600 flex items-center justify-center text-gray-500">
                  <Camera className="w-5 h-5" />
                </div>
              )}
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={photoBusy || submitting}
                className="px-4 py-2 rounded-xl bg-gray-800 text-gray-200 border border-gray-600 hover:bg-gray-700 disabled:opacity-60 text-sm font-semibold"
              >
                {photoBusy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
                {photoDataUrl ? 'Cambiar foto' : 'Tomar / elegir foto'}
              </button>
            </div>
            {photoError && <p className="text-amber-300 text-xs mb-3">{photoError}</p>}
            {photoBusy && <p className="text-gray-500 text-xs mb-3">Procesando foto…</p>}
            <p className="text-gray-500 text-xs mb-4">Opcional — se envía junto con el registro.</p>

            <label className="block text-gray-400 text-sm mb-1 flex items-center gap-1"><Car className="w-3.5 h-3.5" /> Placa</label>
            <input
              type="text"
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              placeholder="Opcional"
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-xl border border-gray-700 focus:border-[#36CCD8] focus:outline-none placeholder-gray-500 mb-4"
            />

            <label className="block text-gray-400 text-sm mb-1">Motivo</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Opcional — Ej: entrega, visita familiar"
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-xl border border-gray-700 focus:border-[#36CCD8] focus:outline-none placeholder-gray-500 mb-4"
            />

            <label className="block text-gray-400 text-sm mb-2">Duración del acceso</label>
            <div className="flex flex-wrap gap-2 mb-5">
              {DURATIONS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setDurationHours(h)}
                  className={`px-4 py-2 rounded-xl font-bold text-sm transition-colors ${
                    durationHours === h
                      ? 'bg-[#36CCD8] text-white shadow-lg'
                      : 'bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700'
                  }`}
                >
                  {h}h
                </button>
              ))}
            </div>

            {error && (
              <div className="rounded-xl bg-red-500/15 border border-red-500/40 text-red-300 text-sm px-4 py-3 mb-4">{error}</div>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={submitting || photoBusy || !visitorName.trim() || !unitId}
              className="w-full py-4 bg-[#36CCD8] text-white font-bold rounded-2xl hover:bg-[#2DB8C4] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
              {submitting ? 'Registrando…' : 'Solicitar aprobación'}
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              <h2 className="text-lg font-bold text-white">Visita registrada</h2>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              {created.visitor_name || visitorName} · {units.find((u) => u.id === unitId)?.label || created.unit_label || ''}
            </p>

            <div className="bg-gray-800 border-2 border-dashed border-[#36CCD8]/60 rounded-2xl p-5 text-center mb-4">
              <p className="text-gray-400 text-xs mb-1">Código de acceso</p>
              <p className="text-2xl font-mono font-bold text-white tracking-widest select-all">{codeString || '—'}</p>
              <span className="inline-block mt-3 text-xs font-bold px-3 py-1 rounded-full bg-amber-500/25 text-amber-300">
                Pendiente de aprobación
              </span>
            </div>

            <div className="rounded-2xl bg-amber-500/10 border border-amber-500/40 p-4 mb-4 flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-200 text-sm">
                Esperando que el residente apruebe la visita. Entrégale este código al visitante; el acceso se activa al aprobarse.
              </p>
            </div>
            <div className="rounded-2xl bg-gray-800 border border-gray-700 p-4 mb-5 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-gray-400 text-xs">
                Si el residente no responde en 15 minutos, la solicitud se cancela automáticamente y deberás registrarla de nuevo.
              </p>
            </div>

            {contactsState !== 'idle' && contactsState !== 'unavailable' && (
              <div className="rounded-2xl bg-gray-800 border border-gray-700 p-4 mb-4">
                <p className="text-gray-300 text-sm font-bold mb-2">Residentes de la unidad</p>
                {contactsState === 'loading' ? (
                  <p className="text-gray-500 text-xs">Cargando contactos…</p>
                ) : unitContacts.length === 0 ? (
                  <p className="text-gray-500 text-xs">No hay contactos de residentes para esta unidad.</p>
                ) : unitContacts.some((c) => c.phone) ? (
                  <ul className="space-y-2">
                    {unitContacts.map((c, i) => (
                      <li key={`${c.name}-${i}`} className="flex items-center justify-between gap-3">
                        <span className="text-gray-200 text-sm truncate">{c.name}</span>
                        {c.phone ? (
                          <a
                            href={`tel:${c.phone}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600/20 text-emerald-300 text-xs font-bold hover:bg-emerald-600/30 flex-shrink-0"
                          >
                            <Phone className="w-3.5 h-3.5" /> Llamar
                          </a>
                        ) : (
                          <span className="text-gray-500 text-xs flex-shrink-0">Sin teléfono</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-xs">Ningún residente de la unidad tiene teléfono registrado.</p>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={handleClose}
              className="w-full py-4 bg-[#36CCD8] text-white font-bold text-lg rounded-2xl hover:bg-[#2DB8C4] shadow-lg"
            >
              Continuar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
